import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  Divider,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RefreshIcon from '@mui/icons-material/Refresh';
import { copyReportToClipboard } from '../utils/reportUtils';
import type { ReportData } from '../services/transcriptionService';
import reportService from '../services/reportService';
import GradingPanel from './GradingPanel';

interface ReportPreviewPanelProps {
  report: ReportData | null;
  sessionId: string;
  onReportGenerated?: (report: ReportData) => void;
  gradingEnabled?: boolean;
  gradingMode?: string;
}

const ReportPreviewPanel: React.FC<ReportPreviewPanelProps> = ({
  report,
  sessionId,
  onReportGenerated,
  gradingEnabled = false,
  gradingMode = 'warn',
}) => {
  const [generating, setGenerating] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const handleCopy = async () => {
    if (!report) return;
    try {
      await copyReportToClipboard(report.report_text);
      setSnackbar({ open: true, message: 'Report copied to clipboard.', severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Failed to copy', severity: 'error' });
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const newReport = await reportService.generateReport({ session_id: sessionId });
      onReportGenerated?.(newReport);
      setSnackbar({ open: true, message: 'Radiology report created successfully.', severity: 'success' });
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Failed to create report';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setGenerating(false);
    }
  };

  if (!report) {
    return (
      <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary" gutterBottom>
          No report available for this dictation session.
        </Typography>
        <Button
          variant="contained"
          onClick={handleGenerate}
          disabled={generating}
          startIcon={generating ? <CircularProgress size={18} /> : <RefreshIcon />}
          sx={{ mt: 1 }}
        >
          {generating ? 'Creating Report...' : 'Create Radiology Report'}
        </Button>
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        >
          <Alert severity={snackbar.severity} variant="filled">
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Paper>
    );
  }

  return (
    <Box>
      {/* Header with metadata */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        {report.cpt_code ? (
          <Chip
            label={`${report.cpt_code} — ${report.cpt_exam_name || ''}`}
            size="small"
            color="primary"
            variant="outlined"
          />
        ) : (
          <Chip label="No CPT Code" size="small" variant="outlined" />
        )}
        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
          Created: {new Date(report.created_at).toLocaleString()}
        </Typography>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {gradingEnabled && report.grading_data && (
        <GradingPanel
          gradingData={report.grading_data}
          enforcementMode={gradingMode}
        />
      )}

      {/* Report text */}
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          maxHeight: '50vh',
          overflow: 'auto',
          backgroundColor: '#fafafa',
          fontFamily: 'monospace',
        }}
      >
        <Typography
          variant="body2"
          sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}
        >
          {report.report_text}
        </Typography>
      </Paper>

      {/* Actions */}
      <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<ContentCopyIcon />}
          onClick={handleCopy}
        >
          Copy to Clipboard
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={generating ? <CircularProgress size={16} /> : <RefreshIcon />}
          onClick={handleGenerate}
          disabled={generating}
        >
          {generating ? 'Recreating...' : 'Recreate Report'}
        </Button>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ReportPreviewPanel;
